/*
 * The swipe chain, Home -> Bio-Bank -> Experiments (decisions D12 and D24 in
 * docs/UX_AUDIT_2026-10-24.md): where a page swipe or the pager goes from
 * here, and how the history moves. Pure; hooks/useHubChain.js acts on it.
 *
 * The chain is registry.swipeChain(user): Home and the hubs the person may
 * open that have a place in it. A physical left-to-right swipe goes to the
 * NEXT stop, right-to-left to the PREVIOUS one -- in Hebrew too.
 *
 * THE HISTORY. Going forward pushes an entry that remembers where it came
 * from (state.hubSwipeFrom). Going back to exactly that page steps the
 * history back instead of pushing, so a swipe there and a swipe back leave the
 * history as it was, and the phone's Back button agrees with the swipes.
 * Home's filters live in its address, so they come back with it.
 */
import { cleanPath } from './registry';

/** The stop for `pathname` in the chain and its neighbours: {index, prev, next}
 *  (index -1, no neighbours, off the chain). */
export function neighbours(pathname, chain) {
  const index = chain.findIndex((stop) => stop.path === cleanPath(pathname));
  if (index < 0) return { index, prev: null, next: null };
  return { index, prev: chain[index - 1] || null, next: chain[index + 1] || null };
}

/**
 * What going `direction` ('next' or 'prev') from `location` ({pathname,
 * search, state}) does: {to, state} to push, {back: true} to step the history
 * back, or null where there is nothing that way. `stop` is where it arrives.
 */
export function historyStep(direction, location, chain) {
  const { prev, next } = neighbours(location.pathname, chain);
  if (direction === 'next' && next) {
    return { stop: next, to: next.path, state: { hubSwipeFrom: `${cleanPath(location.pathname)}${location.search || ''}` } };
  }
  if (direction === 'prev' && prev) {
    const from = location.state?.hubSwipeFrom;
    if (typeof from === 'string' && cleanPath(from) === prev.path) return { stop: prev, back: true };
    return { stop: prev, to: prev.path, state: null };
  }
  return null;
}

/** The label key of a stop: a hub's own, Home's. */
export const stopLabel = (stop) => stop.label || 'nav:home';

// The stop a move is on its way to, so the page that arrives can take focus
// and be announced (components/Layout.js), and only after such a move.
let arriving = null;

export function markArriving(stop) {
  arriving = stop;
}

/** The stop a move was on its way to, if it is `pathname`; read once. */
export function takeArriving(pathname) {
  const stop = arriving;
  arriving = null;
  return stop && stop.path === cleanPath(pathname) ? stop : null;
}
